import { flow, getSnapshot, types } from 'mobx-state-tree';
import { Messenger } from 'http-requests';
import { ConversationDTO } from 'http-requests/messenger';
import {
  Statuses,
  ConversationModel,
  ConversationsParams,
  SearchResultModel,
} from './model';
import { IConversationModel, InitialNewMessagesCounter } from './types';

export const MessengerStore = types
  .model('MessengerStore', {
    requestsStates: types.optional(Statuses, {}),
    conversationsParams: types.optional(ConversationsParams, {}),
    conversations: types.optional(types.array(ConversationModel), []),

    // Поиск
    searchResult: types.optional(SearchResultModel, {}),
    // Выбранное сообщение найденное через поиск
    selectedMessageIdInConversation: types.optional(
      types.maybeNull(types.string),
      null
    ),

    // Выбранный чат
    selectedConversationId: types.optional(types.maybeNull(types.string), null),
    // Чат, если его нет в списке conversations
    unlistedConversation: types.optional(
      types.maybeNull(ConversationModel),
      null
    ),
    hasConversationsToUpload: types.optional(types.boolean, true),
  })
  .views((self) => ({
    get mode(): 'normal' | 'search' {
      return self.searchResult.isSearching ? 'search' : 'normal';
    },
    get conversationsIds(): string[] {
      //TODO: добавить здесь проверку, чтобы если у conversation уже указан каунтер -> НЕ ЗАПРАШИВАТЬ ЕЩЁ РАЗ!
      return self.conversations.map(
        (conversation) => conversation.conversation_id
      );
    },
    get selectedConversation(): IConversationModel | null {
      // Возвращается выбранный чат из списка, иначе отдельная модель
      return (
        self.conversations.find(
          (c) => c.conversation_id === self.selectedConversationId
        ) ??
        self.unlistedConversation ??
        null
      );
    },
    get existingConversationIds() {
      return new Set(self.conversations.map((c) => c.conversation_id));
    },
    getConversationById(id: string): IConversationModel | null {
      const conversation = self.conversations.find(
        (c) => c.conversation_id === id
      );

      if (conversation) {
        return conversation;
      }

      if (self.unlistedConversation?.conversation_id === id) {
        return self.unlistedConversation;
      }

      return null;
    },
  }))
  .actions((self) => ({
    moveConversationToTop(conversationId: string) {
      const index = self.conversations.findIndex(
        (c) => c.conversation_id === conversationId
      );

      if (index === -1) {
        return;
      }

      const conversationToMove = self.conversations[index];
      const rest = self.conversations.filter(
        (c) => c.conversation_id !== conversationId
      );

      self.conversations.replace([conversationToMove, ...rest]);
    },
    setSelectedConversationId(id: string) {
      self.selectedConversationId = id;
    },
    selectConversation(conversation: IConversationModel | null) {
      self.selectedConversationId = conversation?.conversation_id ?? null;
      self.unlistedConversation = null;
      self.selectedMessageIdInConversation = null;

      self.conversations.forEach((c) => {
        c.setDraftShown(
          c.conversation_id !== conversation?.conversation_id &&
            (!!c.newMessageText.trim().length || c.replyMessage !== null) &&
            !c.isOverflown
        );
      });
    },
    selectConversationWithMessage(conversationId: string, messageId: string) {
      self.selectedConversationId = conversationId;
      self.selectedMessageIdInConversation = messageId;
    },
    setUnlistedConversation(
      conversation: IConversationModel,
      messageId?: string
    ) {
      self.unlistedConversation = conversation;
      self.selectedConversationId = conversation.conversation_id;

      if (messageId) {
        self.selectedConversationId = `${conversation.conversation_id}:${messageId}`;
      } else {
        self.selectedConversationId = conversation.conversation_id;
      }
    },
    addConversation(newConversation: ConversationDTO) {
      if (!self.existingConversationIds.has(newConversation.conversation_id)) {
        self.conversations.unshift(ConversationModel.create(newConversation));
      }
    },
    clearConversations() {
      //Когда у нас открыт какой-то чат, а мы переключаем фильтры туда-сюда: прихраняем выбранный чат за пределами списка
      if (self.selectedConversationId) {
        const currentSelected = self.conversations.find(
          (c) => c.conversation_id === self.selectedConversationId
        );

        if (currentSelected) {
          const snapshot = getSnapshot(currentSelected);
          self.unlistedConversation = ConversationModel.create(snapshot as any);
        }
      }

      self.conversations.clear();
    },
    reset() {
      self.conversations.clear();
      self.selectedConversationId = null;
      self.unlistedConversation = null;
    },
  }))
  .actions((self) => ({
    getConversations: flow(function* () {
      try {
        self.requestsStates.getConversations.loadingStates();

        const response: ConversationDTO[] = yield Messenger.GetConversations(
          self.conversationsParams
        );

        const newConversations = response
          .filter((c) => !self.existingConversationIds.has(c.conversation_id))
          .map((c) => ConversationModel.create(c));

        self.conversations.replace([
          ...self.conversations,
          ...newConversations,
        ]);

        //Когда у нас открыт какой-то чат, а мы переключаем фильтры туда-сюда: восстанавливаем данные если выбранный чат ЕСТЬ в списке
        if (
          self.unlistedConversation &&
          self.selectedConversationId &&
          self.existingConversationIds.has(self.selectedConversationId)
        ) {
          const conversationInList = self.conversations.find(
            (c) => c.conversation_id === self.selectedConversationId
          );

          if (conversationInList && self.unlistedConversation) {
            const unlistedSnapshot = getSnapshot(self.unlistedConversation);

            if (!unlistedSnapshot) {
              return;
            }

            if (unlistedSnapshot.messages?.length) {
              conversationInList.messages.replace(
                unlistedSnapshot.messages.map((m: any) => m)
              );
            }

            if (unlistedSnapshot.messagesParams) {
              conversationInList.messagesParams.offset =
                unlistedSnapshot.messagesParams.offset;
            }
            conversationInList.hasMessagesToUpload =
              unlistedSnapshot.hasMessagesToUpload;
            conversationInList.newMessageText = unlistedSnapshot.newMessageText;

            if (unlistedSnapshot.newAttachmentsIds?.length) {
              conversationInList.newAttachmentsIds.replace(
                unlistedSnapshot.newAttachmentsIds
              );
            }

            if (unlistedSnapshot.members?.length) {
              conversationInList.members.replace(
                unlistedSnapshot.members.map((m: any) => m)
              );
            }

            if (unlistedSnapshot.filesIds?.length) {
              conversationInList.filesIds.replace(unlistedSnapshot.filesIds);
            }

            if (unlistedSnapshot.allFiles?.length) {
              conversationInList.allFiles.replace(
                unlistedSnapshot.allFiles.map((f: any) => f)
              );
            }

            if (
              unlistedSnapshot.request &&
              Object.keys(unlistedSnapshot.request).length
            ) {
              conversationInList.request = unlistedSnapshot.request;
            }
          }

          self.unlistedConversation = null;
        }

        self.hasConversationsToUpload =
          response.length === self.conversationsParams.limit;

        self.requestsStates.getConversations.successStates();

        return self.conversations;
      } catch (error) {
        console.error(error);
        self.requestsStates.getConversations.errorStates();
      } finally {
        self.requestsStates.getConversations.finallyStates();
      }
    }),
    getConversation: flow(function* (conversationId: string) {
      try {
        self.requestsStates.getConversation.loadingStates();
        const response = yield Messenger.GetConversation(conversationId);
        self.requestsStates.getConversation.successStates();

        return ConversationModel.create(response);
      } catch (error) {
        self.requestsStates.getConversation.errorStates();
        console.error(error);

        return Promise.reject(error);
      } finally {
        self.requestsStates.getConversation.finallyStates();
      }
    }),
    getConversationsCounters: flow(function* () {
      try {
        if (!self.conversationsIds.length) {
          return;
        }

        self.requestsStates.getConversationsCounters.loadingStates();

        const response: InitialNewMessagesCounter[] =
          yield Messenger.GetConversationsCounters(self.conversationsIds);

        response.forEach((item) => {
          const conversation: IConversationModel | null =
            self.getConversationById(`${item.conversation_id}`);

          if (conversation) {
            conversation.setUnreadMessagesCount(item.unread_messages_amount);
          }
        });

        self.requestsStates.getConversationsCounters.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.requestsStates.getConversationsCounters.errorStates();
      } finally {
        self.requestsStates.getConversationsCounters.finallyStates();
      }
    }),
    createConversation: flow(function* (request_id: string) {
      try {
        self.requestsStates.createConversation.loadingStates();

        const response = yield Messenger.CreateConversation({
          request_id,
        });

        self.requestsStates.createConversation.successStates();

        return response;
      } catch (error) {
        console.error(error);
        self.requestsStates.createConversation.errorStates();

        return Promise.reject(error);
      } finally {
        self.requestsStates.createConversation.finallyStates();
      }
    }),
  }))
  .actions((self) => ({
    openConversationFromSearch: flow(function* (
      conversationId: string,
      messageId: string
    ) {
      self.selectedConversationId = conversationId;
      self.selectedMessageIdInConversation = messageId;

      const existedConversation = self.getConversationById(conversationId);

      try {
        if (!existedConversation) {
          const response: ConversationDTO =
            yield self.getConversation(conversationId);
          const convModel = ConversationModel.create(response);
          self.conversations.unshift(ConversationModel.create(response));

          yield convModel.getMessages();
        } else {
          if (
            existedConversation.messages.length <
              existedConversation.messagesParams.limit &&
            !existedConversation.getMessagesStatuses.pending
          ) {
            yield existedConversation.getMessages();
          }
        }

        return true;
      } catch (error) {
        console.error('openConversationFromSearch error', error);

        return false;
      }
    }),
  }));
